// Single source of truth for the JetStream consumer lifecycle and the
// 8h-lake metadata used by the StatusPill / LakeBanner.
//
// Lifted out of useJsConsumer's local `useState` so the consumer
// bootstrap effect, the StatusPill, and any future lake-age banner all
// read from one store instead of duplicating useState branches.

import { create } from 'zustand';

// 'no-scope' = consumer can't subscribe because no company is selected
// (admin viewing "all companies"). Distinct from 'idle' so the UI can
// surface a CTA ("Pick a company to see live data") instead of an
// infinite spinner. See wip-dashboard-first-paint-guarantee.md.
export type ConnStatus = 'idle' | 'no-scope' | 'connecting' | 'live' | 'reconnecting' | 'error';

interface ConnectionState {
  status: ConnStatus;
  /** Stream MaxAge in ms — populated from getStreamInfo() at bootstrap. */
  lakeMaxAgeMs: number;
  /** Timestamp of the oldest message in the lake. Used to banner
   *  "Showing X hours of history available" when the lake is shorter than
   *  what the client requested. */
  lakeFirstTimeMs: number;
  /** Last connect / consumer error, sanitized of creds. Surfaced in toasts. */
  lastError: string | null;

  setStatus: (s: ConnStatus) => void;
  setLakeInfo: (lakeMaxAgeMs: number, lakeFirstTimeMs: number) => void;
  setError: (msg: string | null) => void;
  reset: () => void;
}

export const useConnectionStore = create<ConnectionState>((set) => ({
  status:           'idle',
  lakeMaxAgeMs:     0,
  lakeFirstTimeMs:  0,
  lastError:        null,

  setStatus:    (status)               => set({ status }),
  setLakeInfo:  (lakeMaxAgeMs, lakeFirstTimeMs) => set({ lakeMaxAgeMs, lakeFirstTimeMs }),
  setError:     (lastError)            => set({ lastError }),
  reset:        ()                     => set({ status: 'idle', lakeMaxAgeMs: 0, lakeFirstTimeMs: 0, lastError: null }),
}));

/** True when the lake has fewer messages than the 8h client window. */
export function lakeIsShort(lakeFirstTimeMs: number): boolean {
  if (lakeFirstTimeMs === 0) return false;
  return lakeFirstTimeMs > Date.now() - 8 * 60 * 60 * 1000;
}
